/**
 * Normalize an error from fetch/axios/anywhere into a single shape we can
 * show to the user without leaking internals.
 *
 * Backend source-of-truth (when DRF custom_exception_handler is in place):
 *   { code, message, field?, details?, request_id? }
 *
 * Falls back to legacy DRF shapes:
 *   { detail: '...' }
 *   { field: ['msg'], other_field: ['msg'] }
 *   ['msg']
 *   bare string
 *
 * Returns:
 *   { message, code?, field?, requestId?, status?, isNetworkError, isUserFacing }
 */

const NETWORK_ERROR_MESSAGE = 'Network error. Check your connection and try again.';

export const REQUEST_TOO_LARGE_MESSAGE =
    'That upload is too large. Please use a smaller file (or fewer files) and try again.';

const STATUS_FALLBACK = {
    400: 'That request was invalid. Please review and try again.',
    401: 'Your session expired. Please sign in again.',
    403: "You don't have permission to do that.",
    404: "We couldn't find what you were looking for.",
    408: 'The request took too long. Please try again.',
    409: 'That conflicted with the current state. Please refresh and try again.',
    413: REQUEST_TOO_LARGE_MESSAGE,
    422: 'Some of the information you provided isn’t valid.',
    429: 'You’re doing that too fast. Please wait a moment and try again.',
    500: "We couldn't load this right now. Please try again in a moment.",
    502: 'Our service is briefly unreachable. Please try again in a moment.',
    503: 'Our service is briefly unreachable. Please try again in a moment.',
    504: 'Our service is briefly unreachable. Please try again in a moment.',
};

const GENERIC_FALLBACK = 'Something went wrong with that request.';

const isGenericMessage = (message) =>
    !message || message === GENERIC_FALLBACK || message === 'Request failed';

export function extractApiError(err) {
    if (!err) {
        return {
            message: GENERIC_FALLBACK,
            isNetworkError: false,
            isUserFacing: false,
        };
    }

    // Errors created by our fetchWithErrors helper carry response info.
    const status = err.status ?? err.response?.status;
    const data = err.data ?? err.response?.data;

    // No status at all + a real Error: probably a network/JS error.
    if (status === undefined) {
        if (
            err.name === 'TypeError' ||
            err.name === 'NetworkError' ||
            (typeof navigator !== 'undefined' && !navigator.onLine)
        ) {
            return {
                message: NETWORK_ERROR_MESSAGE,
                isNetworkError: true,
                isUserFacing: false,
            };
        }
        const pulled = pullMessage(err);
        if (pulled.message) {
            return {
                message: pulled.message,
                code: pulled.code,
                field: pulled.field,
                requestId: pulled.requestId,
                isNetworkError: false,
                isUserFacing: true,
            };
        }
        if (err instanceof Error) {
            return {
                message: err.message || GENERIC_FALLBACK,
                isNetworkError: false,
                isUserFacing: false,
            };
        }
        if (typeof err === 'string' && err.trim()) {
            return {
                message: err,
                isNetworkError: false,
                isUserFacing: false,
            };
        }
        return {
            message: GENERIC_FALLBACK,
            isNetworkError: false,
            isUserFacing: false,
        };
    }

    // 413 comes from nginx (client_max_body_size) with an HTML body — never
    // surface that markup as the message.
    if (status === 413) {
        return {
            message: REQUEST_TOO_LARGE_MESSAGE,
            status,
            isNetworkError: false,
            isUserFacing: true,
        };
    }

    const pulled = pullMessage(data);
    const message = pulled.message || STATUS_FALLBACK[status] || err.message || GENERIC_FALLBACK;

    return {
        message,
        code: pulled.code,
        field: pulled.field,
        requestId: pulled.requestId,
        status,
        isNetworkError: false,
        isUserFacing: !!status && status >= 400 && status < 500 && !!pulled.message,
    };
}

function pullMessage(data) {
    if (!data) return {};

    if (typeof data === 'string') {
        return { message: data };
    }

    if (Array.isArray(data)) {
        const first = data.find((v) => typeof v === 'string');
        return first ? { message: String(first) } : {};
    }

    if (typeof data === 'object') {
        // New contract from DRF custom_exception_handler
        if (typeof data.message === 'string') {
            return {
                message: data.message,
                code: typeof data.code === 'string' ? data.code : undefined,
                field: typeof data.field === 'string' ? data.field : undefined,
                requestId: typeof data.request_id === 'string' ? data.request_id : undefined,
            };
        }

        if (typeof data.error === 'string') {
            return {
                message: data.error,
                code: typeof data.code === 'string' ? data.code : undefined,
                requestId: typeof data.request_id === 'string' ? data.request_id : undefined,
            };
        }

        // Legacy DRF: { detail: 'msg' }
        if (typeof data.detail === 'string') {
            return { message: data.detail };
        }

        // Legacy DRF validation: { field_name: ['msg'], ... }
        for (const [key, value] of Object.entries(data)) {
            if (key === 'request_id') continue;
            const inner = pullMessage(value);
            if (inner.message) {
                return {
                    message: inner.message,
                    field: key === 'non_field_errors' ? undefined : key,
                };
            }
        }
    }

    return {};
}

/**
 * Get just the user-facing message string. Pass an optional fallback for when
 * the error gave us no useful info at all.
 */
export function apiErrorMessage(err, fallback) {
    const extracted = extractApiError(err);
    if (fallback && isGenericMessage(extracted.message)) {
        return fallback;
    }
    return extracted.message || fallback || GENERIC_FALLBACK;
}
